import * as uuid from 'uuid'
import type { EpisodeOfCare } from '~/repository/types/episodeOfCare'
import type { LivingPlace } from '~/repository/types/patient/livingPlace'

export function getParticipantList(createdContactPointIds: string[] = [], data: any) {
  const participants = createdContactPointIds.map((id) => ({ entityId: id, entityType: 'contactPoint' }))

  const nonProfessionalList = data?.encounter_participants_non_professional || []
  const professionalList = data?.encounter_participants_professionnal || []

  if (Array.isArray(nonProfessionalList) && nonProfessionalList?.length) {
    participants.push(
      ...nonProfessionalList
        .filter((i) => i.hasOwnProperty('id') && i.id !== null)
        .map((i) => ({
          entityId: i.id,
          entityType: i.entityType,
        })),
    )
  }

  if (Array.isArray(professionalList) && professionalList?.length) {
    participants.push(
      ...professionalList
        .filter((i) => i.hasOwnProperty('id') && i.id !== null)
        .map((i) => ({
          entityId: i.id,
          entityType: i.entityType,
        })),
    )
  }

  return participants
}

export async function updateContactPoint(patientId: string, patientStore: any, data: any) {
  try {
    const contactToAdd: any = []
    const { information, revision } = await patientStore.getInformation(patientId)
    const existingContacts = information?.familyCircle?.contacts

    // handling non professional contact points
    const nonProfessionalList = data?.encounter_participants_non_professional

    if (Array.isArray(nonProfessionalList) && nonProfessionalList?.length) {
      nonProfessionalList
        .filter(({ entityType }: any) => !entityType || entityType === 'contactPoint')
        .forEach((contact) => {
          const existingContactIndex = existingContacts?.findIndex((c: any) => c.id === contact.id)
          if (existingContacts && existingContactIndex !== -1) {
            // Update existing contact
            existingContacts[existingContactIndex] = {
              ...existingContacts[existingContactIndex],
              lastName: contact.lastName,
              firstName: contact.firstName,
              relationship: contact.relationship,
              type: contact.type,
              telephone: contact.phoneNumber || {},
            }
          } else {
            // Add new contact
            contactToAdd.push({
              id: uuid.v4(),
              kind: 'nonProfessional',
              lastName: contact.lastName,
              firstName: contact.firstName,
              relationship: contact.relationship,
              type: contact.type,
              telephone: contact.phoneNumber || {},
            })
          }
        })
    }

    // handling professional contact points
    const professionalList = data?.encounter_participants_professionnal

    if (Array.isArray(professionalList) && professionalList?.length) {
      professionalList
        .filter(({ entityType }: any) => !entityType || entityType === 'contactPoint')
        .forEach((contact) => {
          const existingContactIndex = existingContacts?.findIndex((c: any) => c.id === contact.id)
          if (existingContacts && existingContactIndex !== -1) {
            // Update existing contact
            existingContacts[existingContactIndex] = {
              ...existingContacts[existingContactIndex],
              lastName: contact.lastName,
              firstName: contact.firstName,
              type: contact.type,
              ...(contact.profession ? { job: contact.profession } : {}),
              ...(contact.rpps ? { rpps: contact.rpps } : {}),
            }
          } else {
            // Add new contact
            contactToAdd.push({
              id: uuid.v4(),
              kind: 'professional',
              lastName: contact.lastName,
              firstName: contact.firstName,
              type: contact.type,
              ...(contact.profession ? { job: contact.profession } : {}),
              ...(contact.rpps ? { rpps: contact.rpps } : {}),
            })
          }
        })
    }

    const newInformation = {
      ...information,
      familyCircle: {
        ...(information?.familyCircle ? information?.familyCircle : {}),
        contacts: [...(information?.familyCircle?.contacts ? information?.familyCircle?.contacts : []), ...contactToAdd],
      },
    }

    const params = {
      revision,
    }

    await patientStore.patchInformation(patientId, params, { information: newInformation })

    return contactToAdd
  } catch (error) {
    // contact point update error handling
    throw error
  }
}

export function getInitiator(addedContactPoints: any, data: any) {
  const participant = [...data?.encounter_participants_professionnal, ...data?.encounter_participants_non_professional].find(
    ({ hasInitiated }) => hasInitiated === true,
  )

  if (!participant) {
    return
  }

  if (participant.id && participant.entityType) {
    return {
      entityId: participant.id,
      entityType: participant.entityType,
    }
  }

  const contactPoint = addedContactPoints.find(
    (i: { lastName: string; firstName: string; type: string; [key: string]: any }) =>
      i.lastName === participant.lastName && i.firstName === participant.firstName && i.type === participant.type,
  )

  if (contactPoint) {
    return {
      entityId: contactPoint.id,
      entityType: 'contactPoint',
    }
  }
}

export function updatePanelState(survey: any) {}

export function deepEqual(value1: any, value2: any) {
  if (value1 === value2) {
    return true
  }

  if (value1 === null || value2 === null || typeof value1 !== 'object' || typeof value2 !== 'object') {
    return false
  }

  const keys1 = Object.keys(value1)
  const keys2 = Object.keys(value2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(value1[key], value2[key])) {
      return false
    }
  }

  return true
}

// PROCESSING SURVEY ANSWER TO GENERATE DOCUMENT
type DictionaryItem = { value: string; text: string }

const ENCOUNTER_TYPE_DICTIONARY: DictionaryItem[] = [
  {
    value: 'teleConsultation',
    text: 'Téléconsultation',
  },
  {
    value: 'teleExpertise',
    text: 'Téléexpertise',
  },
  {
    value: 'officeConsultation',
    text: 'Consultation en cabinet',
  },
  {
    value: 'homeConsultation',
    text: 'Consultation à domicile',
  },
  {
    value: 'homeHealthVisit',
    text: 'Visite à domicile',
  },
  {
    value: 'callWithRelative',
    text: 'Appel entourage',
  },
  {
    value: 'callWithPatient',
    text: 'Appel patient',
  },
  {
    value: 'callWithPractitionerInCharge',
    text: 'Appel professionnel de santé',
  },
  {
    value: 'emailCommunication',
    text: 'Echange par email',
  },
]

const PRACTITIONER_PROFESSION_DICTIONARY: DictionaryItem[] = [
  {
    value: 'doctor',
    text: 'Médecin',
  },
  {
    value: 'nursingAssistant',
    text: 'Infirmière',
  },
  {
    value: 'nurse',
    text: 'Aide soignante',
  },
]

const CONTACT_POINT_TYPE_DICTIONARY: DictionaryItem[] = [
  {
    value: 'trustedPerson',
    text: 'Personne de confiance',
  },
  {
    value: 'primaryCaregiver',
    text: 'Aidant de confiance',
  },
  {
    value: 'emergencyContact',
    text: 'Contact d’urgence',
  },
  {
    value: 'emergency-contact',
    text: 'Contact d’urgence',
  },
  {
    value: 'tutor',
    text: 'Tuteur / Tutrice',
  },
  {
    value: 'contactPersonForReturn',
    text: 'Personne contact pour le retour',
  },
  {
    value: 'doctor',
    text: 'Médecin traitant',
  },
  {
    value: 'personToContactInCaseOfEmergency',
    text: 'Personne à contacter en cas d’urgence',
  },
  {
    value: 'episodeOfCareRequestor',
    text: "Demandeur de l'épisode de soin",
  },
  {
    value: 'contactPersonForReturn',
    text: 'Personne contact pour le retour',
  },
  {
    value: 'pharmacist',
    text: 'Pharmacien',
  },
  {
    value: 'idelssiad',
    text: 'IDEL / SSIAD',
  },
  {
    value: 'spasad',
    text: 'SAP / SAD',
  },
  {
    value: 'laboratory',
    text: 'Laboratoire',
  },
  {
    value: 'specialistDoctor',
    text: 'Médecin spécialiste',
  },
  {
    value: 'physiotherapist',
    text: 'Kiné',
  },
  {
    value: 'ambulance',
    text: 'Ambulance',
  },
  {
    value: 'imagingCentre',
    text: 'Centre d’imagerie',
  },
  {
    value: 'referralHospital',
    text: 'Hôpital de référence',
  },
]

const CONTACT_POINT_RELATIONSHIP_DICTIONARY: DictionaryItem[] = [
  {
    value: 'parent',
    text: 'Parent',
  },
  {
    value: 'child',
    text: 'Enfant',
  },
  {
    value: 'siblings',
    text: 'Fratrie',
  },
  {
    value: 'grandChild',
    text: 'Petit-enfant',
  },
  {
    value: 'grandParent',
    text: 'Grand-parent',
  },
  {
    value: 'nephewNiece',
    text: 'Neveu / Nièce',
  },
  {
    value: 'uncleAunt',
    text: 'Oncle / Tante',
  },
  {
    value: 'greatGrandchild',
    text: 'Arrière-petit-enfant',
  },
  {
    value: 'greatGrandparent',
    text: 'Arrière-grand-parent',
  },
  {
    value: 'cousin',
    text: 'Cousin germain',
  },
  {
    value: 'greatUncleGreatAunt',
    text: 'Grand-oncle / Grande-tante',
  },
  {
    value: 'spouse',
    text: 'Epoux / Epouse',
  },
  {
    value: 'concubine',
    text: 'Concubin(e)',
  },
]

const CONTACT_POINT_PROFESSION_DICTIONARY: DictionaryItem[] = [
  {
    value: 'doctor',
    text: 'Médecin',
  },
  {
    value: 'nursingAssistant',
    text: 'Infirmière',
  },
  {
    value: 'nurse',
    text: 'Aide soignante',
  },
]

const LIVING_PLACE_TYPE_DICTIONARY: DictionaryItem[] = [
  {
    value: 'house',
    text: 'Domicile maison',
  },
  { value: 'flat', text: 'Domicile appartement' },
  { value: 'caravan', text: 'Domicile caravane' },
  { value: 'barge', text: 'Domicile péniche' },
  { value: 'nursingHome', text: 'EHPAD' },
  { value: 'residenceService', text: 'Résidence de service' },
  { value: 'hotel', text: 'Hôtel' },
]

const PHONE_NUMBER_TYPE_DICTIONARY: DictionaryItem[] = [
  { value: 'mobile', text: 'Portable' },
  { value: 'landline', text: 'Fixe' },
]

const MISCALLANEOUS_DICTIONARY: DictionaryItem[] = [
  {
    value: 'yes',
    text: 'Oui',
  },
  {
    value: 'no',
    text: 'Non',
  },
  {
    value: 'often',
    text: 'Souvent',
  },
  {
    value: 'sometimes',
    text: 'Parfois',
  },
  {
    value: 'exceptional',
    text: 'Exceptionnel',
  },
  {
    value: 'never',
    text: 'Jamais',
  },
  {
    value: 'weaned',
    text: 'Sevré',
  },
  {
    value: 'other',
    text: 'Autre',
  },
]

const dictionaries: { [key: string]: DictionaryItem[] } = {
  PRACTITIONER_PROFESSION_DICTIONARY,
  CONTACT_POINT_TYPE_DICTIONARY,
  CONTACT_POINT_RELATIONSHIP_DICTIONARY,
  CONTACT_POINT_PROFESSION_DICTIONARY,
  LIVING_PLACE_TYPE_DICTIONARY,
  PHONE_NUMBER_TYPE_DICTIONARY,
  MISCALLANEOUS_DICTIONARY,
  ENCOUNTER_TYPE_DICTIONARY,
}

function getTextFromDictionary(dictionaryName: string, value?: string): string | null {
  const dictionary = dictionaries[dictionaryName]
  if (!dictionary) return null

  const item = dictionary.find((item) => item.value === value)
  return item ? item.text : null
}

function formatDateAndHour(input: string | Date): { formattedDate: string; formattedHour: string } {
  const date = input ? (typeof input === 'string' ? new Date(input) : input) : new Date()

  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear().toString().slice(-2)
  const hour = date.getHours().toString().padStart(2, '0')

  const formattedDate = `${day}/${month}/${year}`
  const formattedHour = `${hour}h`

  return { formattedDate, formattedHour }
}

const relationShipLabel = (items: any, value: any) => {
  const filterRelationShip = items.filter((re: any) => re.code === value).pop()
  return filterRelationShip?.translations?.[0]?.name || ''
}

export function processSurveyAnswer({
  answers,
  information,
  livingPlace,
  eoc,
  relationLabel,
}: {
  answers: any
  information: any
  livingPlace: LivingPlace | null
  eoc: EpisodeOfCare | null
  relationLabel: any
}) {
  const processedAnswers: any = {}
  try {
    const encounterPraticipantsProfessinnal = answers?.encounter_participants_professionnal || []
    const encounterPraticipantsNonProfessinnal = answers?.encounter_participants_non_professional || []
    const initiator = [...encounterPraticipantsProfessinnal, ...encounterPraticipantsNonProfessinnal]?.find(
      ({ hasInitiated }) => hasInitiated === true,
    )
    const practitioners = encounterPraticipantsProfessinnal?.filter(
      ({ entityType }: { entityType: string }) => entityType === 'practitioner',
    )
    const professionalContactPoints = encounterPraticipantsProfessinnal?.filter(
      ({ entityType }: { entityType: string }) => entityType !== 'practitioner',
    )
    const contactPoints = encounterPraticipantsNonProfessinnal?.filter(
      ({ entityType }: { entityType: string }) => entityType !== 'patient',
    )

    // EOC information
    processedAnswers.encoutner_eoc_reason = eoc?.reason || 'aucun eoc'

    // Practitioner information
    if (practitioners?.length) {
      processedAnswers.practitioner_fullName = `${practitioners[0]?.firstName} ${practitioners[0]?.lastName}`
      processedAnswers.practitioner_profession = practitioners[0]?.lastName
      processedAnswers.practitioner_rpps = practitioners[0]?.rpps
    }

    processedAnswers.encounter_assistant = contactPoints?.length
      ? 'Assistée par un proche'
      : professionalContactPoints?.length
        ? 'Assistée par un professionnel de santé'
        : '-'

    // Contact point details
    if (contactPoints?.length) {
      const contact = contactPoints[0]
      const translatedRelationship = relationShipLabel(relationLabel, contact?.relationship)
      const translatedType = getTextFromDictionary('CONTACT_POINT_TYPE_DICTIONARY', contact?.type)
      const formattedLastname = contact?.lastName ? `Nom : ${contact?.lastName}` : null
      const formattedFirstname = contact?.firstName ? `Prénom : ${contact?.firstName}` : null
      const formattedType = translatedType ? `Type de relation : ${translatedType}` : null
      const formattedRelationship = translatedRelationship ? `Nature de la relation : ${translatedRelationship}` : null
      const formattedPhoneNumber = contact?.phoneNumber ? `Numéro de téléphone : ${contact?.phoneNumber}` : null

      const detailsArray = [
        formattedLastname,
        formattedFirstname,
        formattedType,
        formattedRelationship,
        formattedPhoneNumber,
      ]

      const nonNullDetails = detailsArray.filter((detail) => detail !== null)

      if (nonNullDetails.length) {
        processedAnswers.encounter_assistant_details = nonNullDetails.join('\n')
      }
    }

    // Professional contact point details
    if (professionalContactPoints?.length && !contactPoints?.length) {
      const contact = professionalContactPoints[0]
      const translatedType = getTextFromDictionary('CONTACT_POINT_TYPE_DICTIONARY', contact?.type)
      const translatedProfession = getTextFromDictionary('CONTACT_POINT_PROFESSION_DICTIONARY', contact?.profession)
      const formattedLastname = contact?.lastName ? `Nom : ${contact?.lastName}` : null
      const formattedFirstname = contact?.firstName ? `Prénom : ${contact?.firstName}` : null
      const formattedType = translatedType ? `Type de relation : ${translatedType}` : null
      const formattedProfession = translatedProfession ? `Profession : ${translatedProfession}` : null
      const formattedRPPS = contact?.rpps ? `RPPS : ${contact?.rpps}` : null

      const detailsArray = [formattedLastname, formattedFirstname, formattedType, formattedProfession, formattedRPPS]

      const nonNullDetails = detailsArray.filter((detail) => detail !== null)
      if (nonNullDetails.length) {
        processedAnswers.encounter_assistant_details = nonNullDetails.join('\n')
      }
    }

    // Initiator information
    if (initiator) {
      // French translation for the initiator entity type
      const formattedInitiatorType =
        initiator?.entityType === 'patient' ? 'patient' : initiator?.entityType === 'practitioner' ? 'PRO EPOCA' : 'contact'
      processedAnswers.encounter_initiator = `${initiator?.firstName} ${initiator?.lastName} (${formattedInitiatorType})`
    }

    // Patient social secutiry number
    processedAnswers.information_patientSocialSecurityNumber =
      information?.identityAdministrative?.administrative?.patientSocialSecurityNumber || '-'

    // Patient living place
    processedAnswers.patient_livingPlace_type =
      getTextFromDictionary(
        'LIVING_PLACE_TYPE_DICTIONARY',
        (livingPlace?.type as { value: string; customValue: any })?.value,
      ) || '-'
    processedAnswers.patient_livingPlace_address_line = livingPlace?.address?.lines?.join(' - ')
    processedAnswers.patient_livingPlace_address_postalCode = livingPlace?.address?.postalCode
    processedAnswers.patient_livingPlace_address_city = livingPlace?.address?.city

    // Patient contacts mobile and landline
    processedAnswers.information_phoneNumbers = information?.identityAdministrative?.contactInformation?.telephones?.length
      ? information?.identityAdministrative?.contactInformation?.telephones
          ?.map(
            ({ number, type }: { number: string; type: string }) =>
              `${number} (${getTextFromDictionary('PHONE_NUMBER_TYPE_DICTIONARY', type)})`,
          )
          ?.join(' | ')
      : '-'

    // Medical record informations
    processedAnswers.information_medicalRecord_medicalBackground_medicalBackgrounds =
      answers['encounter_medicalRecord_medicalBackground_medicalBackgrounds'] || '-'
    processedAnswers.information_medicalRecord_medicalBackground_surgicalBackgrounds =
      answers['encounter_medicalRecord_medicalBackground_surgicalBackgrounds'] || '-'
    processedAnswers.information_medicalRecord_medicalBackground_familyBackgrounds =
      answers['encounter_medicalRecord_medicalBackground_familyBackgrounds'] || '-'

    processedAnswers.information_medicalRecord_medicalBackground_tobacco =
      answers['encounter_medicalRecord_medicalBackground_tobacco'] || '-'

    processedAnswers.information_medicalRecord_medicalBackground_tobaccoPacksPerWeek =
      answers['encounter_medicalRecord_medicalBackground_tobaccoPacksPerWeek'] || '-'

    processedAnswers.information_medicalRecord_medicalBackground_alcohol =
      answers['encounter_medicalRecord_medicalBackground_alcohol'] || '-'

    processedAnswers.information_medicalRecord_medicalBackground_alcoholDrinksPerWeek =
      answers['encounter_medicalRecord_medicalBackground_alcoholDrinksPerWeek'] || '-'

    processedAnswers.information_medicalRecord_medicalBackground_drugs =
      answers['encounter_medicalRecord_medicalBackground_drugs'] || '-'

    // Processing dates to split date in date and hours and format in french format
    const { formattedDate: start_date, formattedHour: start_hour } = formatDateAndHour(
      answers['encounter_startedAt'] as string,
    )
    const { formattedDate: end_date, formattedHour: end_hour } = formatDateAndHour(answers['encounter_endedAt'] as string)
    const { formattedDate: finalize_date, formattedHour: finalize_hour } = formatDateAndHour(new Date())
    processedAnswers.start_date = start_date
    processedAnswers.start_hour = start_hour
    processedAnswers.end_date = end_date
    processedAnswers.end_hour = end_hour
    processedAnswers.finalize_date = finalize_date
    processedAnswers.finalize_hour = finalize_hour

    processedAnswers.encounter_reason = `${answers?.encounter_reason_use} - ${answers?.encounter_reason_value}`
    processedAnswers.encounter_translatedType = getTextFromDictionary('ENCOUNTER_TYPE_DICTIONARY', answers['encounter_type'])
  } catch (error) {
    console.error(error)
  }

  return { ...answers, ...processedAnswers }
}
